import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle2, Undo2, Filter, Calendar, User, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DoneTasksReportPage() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  
  // Filters
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterTaskType, setFilterTaskType] = useState('all');

  useEffect(() => {
    fetchEmployees();
    fetchReport();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/hr-employees?staff_type=Office%20Staff`, {
        headers: getAuthHeader()
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('from_date', fromDate);
      params.append('to_date', toDate);
      if (filterEmployee !== 'all') {
        params.append('employee_id', filterEmployee);
      }
      if (filterTaskType !== 'all') {
        params.append('task_type', filterTaskType);
      }

      const response = await axios.get(`${API_URL}/reports/done-tasks?${params.toString()}`, {
        headers: getAuthHeader()
      });
      
      setTasks(response.data.tasks || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to fetch done tasks report');
    } finally {
      setLoading(false);
    }
  };

  const handleUndoTask = async (taskId, taskType) => {
    try {
      await axios.patch(
        `${API_URL}/tasks/${taskId}/undo-complete?task_type=${taskType}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Task marked as pending again');
      fetchReport(); // Refresh the list
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to undo task';
      toast.error(message);
    }
  };

  // Check if user can undo a task (same day only, and either admin or own task)
  const canUndoTask = (task) => {
    // Check if completed today
    const completedDate = new Date(task.completed_at).toDateString();
    const today = new Date().toDateString();
    if (completedDate !== today) return false;
    
    // Users with delegation access can undo any task
    if (user?.is_owner || user?.allowed_modules?.includes('delegation')) return true;
    
    // Employee can only undo their own tasks
    return task.doer_id === user?.id;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="done-tasks-report-title">Done Tasks Report</h1>
            <p className="text-muted-foreground">View all completed tasks with filters</p>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* From Date */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  From Date
                </Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  data-testid="filter-from-date"
                />
              </div>
              
              {/* To Date */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  To Date
                </Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  data-testid="filter-to-date"
                />
              </div>
              
              {/* Employee Filter */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Doer
                </Label>
                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger data-testid="filter-employee">
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Task Type Filter */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  Task Type
                </Label>
                <Select value={filterTaskType} onValueChange={setFilterTaskType}>
                  <SelectTrigger data-testid="filter-task-type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="delegation">Delegation</SelectItem>
                    <SelectItem value="checklist">Checklist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button onClick={fetchReport} disabled={loading} data-testid="apply-filters-btn">
                {loading ? 'Loading...' : 'Apply Filters'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Card */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Done</p>
                    <p className="text-3xl font-bold text-green-600">{summary.total_done}</p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Delegation Tasks</p>
                    <p className="text-3xl font-bold text-blue-600">{summary.delegation_count}</p>
                  </div>
                  <Badge variant="outline" className="text-blue-600">Delegation</Badge>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Checklist Tasks</p>
                    <p className="text-3xl font-bold text-purple-600">{summary.checklist_count}</p>
                  </div>
                  <Badge variant="outline" className="text-purple-600">Checklist</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tasks Table */}
        <Card>
          <CardHeader>
            <CardTitle>Completed Tasks</CardTitle>
            <CardDescription>
              {fromDate === toDate 
                ? `Tasks completed on ${formatDate(fromDate)}`
                : `Tasks completed from ${formatDate(fromDate)} to ${formatDate(toDate)}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-muted-foreground">No completed tasks found for the selected filters</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Doer</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Completed At</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={`${task.task_type}-${task.id}`}>
                      <TableCell>
                        <div className="font-medium">{task.title}</div>
                        {task.description && (
                          <div className="text-xs text-muted-foreground">{task.description.substring(0, 60)}...</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          task.task_type === 'delegation' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                        }>
                          {task.task_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>{task.doer_name}</div>
                        {task.doer_code && (
                          <div className="text-xs text-muted-foreground">{task.doer_code}</div>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(task.due_date)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle2 className="w-4 h-4" />
                          {formatDateTime(task.completed_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          task.priority === 'critical' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }>
                          {task.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canUndoTask(task) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUndoTask(task.id, task.task_type)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            data-testid={`undo-btn-${task.id}`}
                          >
                            <Undo2 className="w-4 h-4 mr-1" />
                            Undo
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
